import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react';
import { useCallback, useEffect, useId, useRef } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
function focusables(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (el) => el.offsetParent !== null || el === document.activeElement,
  );
}

/** Câu hỏi mặc định khi đóng hộp thoại đang nhập dở (mockup v152 "Bỏ các thay đổi chưa lưu?"). */
export const DISCARD_CHANGES_PROMPT = 'Bỏ các thay đổi chưa lưu?';

/**
 * Hộp thoại. T6-A (S-8): `confirmClose` — khi có (chuỗi câu hỏi, hoặc `true` = câu mặc định) thì đóng bằng Esc / nút ✕ phải xác nhận.
 * 03/11/2026 (người dùng yêu cầu): bấm ra NỀN ngoài hộp KHÔNG đóng — chỉ nút (✕, Hủy, Lưu…) hoặc Esc; tránh mất dữ liệu đang nhập vì lỡ tay.
 * Truyền `confirmClose={dirty}` (hoặc câu riêng khi dirty) — nút "Hủy" ở footer tự gọi `useDirtyGuard().confirmDiscard()` (admin-console).
 * `wide` = hộp rộng cho form nhiều cột; `xl` = gần toàn màn (bảng nhiều cột như dòng báo giá).
 * `width` = bề rộng CSS riêng (vd `min(96vw, max(860px, 75vw))`), ghi đè wide/xl — KOVIX-3160.
 */
export function Modal({
  open,
  title,
  onClose,
  footer,
  children,
  confirmClose,
  wide,
  xl,
  width,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  footer?: ReactNode | undefined;
  children: ReactNode;
  confirmClose?: boolean | string | null | undefined;
  wide?: boolean | undefined;
  xl?: boolean | undefined;
  width?: string | undefined;
}) {
  const requestClose = useCallback(() => {
    if (confirmClose) {
      const q = typeof confirmClose === 'string' ? confirmClose : DISCARD_CHANGES_PROMPT;
      if (typeof window !== 'undefined' && !window.confirm(q)) return;
    }
    onClose();
  }, [confirmClose, onClose]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, requestClose]);
  // UI test 01/10/2026 (WCAG 2.4.3): mở → focus vào ô nhập đầu tiên (hoặc chính hộp); Tab/Shift+Tab xoay vòng trong hộp; đóng → trả focus về nút đã mở.
  const boxRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const t = window.setTimeout(() => {
      const box = boxRef.current;
      if (!box || box.contains(document.activeElement)) return; // con tự autoFocus → giữ
      const first =
        box.querySelector<HTMLElement>(
          '.kx-modal-body input:not([type="hidden"]):not([disabled]), .kx-modal-body select:not([disabled]), .kx-modal-body textarea:not([disabled])',
        ) ?? focusables(box).find((el) => !el.classList.contains('kx-modal-x'));
      (first ?? box).focus();
    }, 0);
    return () => {
      window.clearTimeout(t);
      if (opener?.isConnected) opener.focus();
    };
  }, [open]);
  const onTrap = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab' || !boxRef.current) return;
    const els = focusables(boxRef.current);
    const first = els[0];
    const last = els[els.length - 1];
    if (!first || !last) {
      e.preventDefault();
      return;
    }
    if (e.shiftKey && (document.activeElement === first || document.activeElement === boxRef.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };
  if (!open) return null;
  return (
    <div className="kx-modal-bg" role="presentation">
      <div
        ref={boxRef}
        tabIndex={-1}
        onKeyDown={onTrap}
        className={`kx-modal${xl ? ' xl' : wide ? ' wide' : ''}`}
        style={width ? { width } : undefined}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="kx-modal-head">
          <span id={titleId}>{title}</span>
          <button type="button" className="kx-modal-x" aria-label="Đóng" onClick={requestClose}>
            ✕
          </button>
        </div>
        <div className="kx-modal-body">{children}</div>
        {footer ? <div className="kx-modal-foot">{footer}</div> : null}
      </div>
    </div>
  );
}
